import {
  containsRectangle,
  isValidRectangle,
  MARKUP_COORDINATE_ROUNDING_TOLERANCE,
} from 'features/documentMarkup/editor/markupGeometry'
import type { MarkupRectangle } from 'features/documentMarkup/editor/types'
import type { DocumentDrawing } from 'features/libra/document/LibraDocumentContext'

export type MeasuredImage = {
  documentId: string
  width: number
  height: number
}
export type PixelImageDimensions = Pick<MeasuredImage, 'width' | 'height'>
export type PixelRectanglePolicy = {
  documentId: string
  boundary?: MarkupRectangle
  readonly obstacles: readonly { id: string; rectangle: MarkupRectangle }[]
}
type PixelRectangleValidationError =
  | 'invalid-rectangle'
  | 'invalid-image-size'
  | 'minimum-size'
  | 'outside-image'

export const MINIMUM_NATURAL_RECTANGLE_SIZE = 20
export const PIXEL_RECTANGLE_SIZE_TOLERANCE = 1e-6

export const pixelRectangleMeetsMinimumSize = (rectangle: MarkupRectangle) =>
  rectangle.x_max - rectangle.x_min >=
    MINIMUM_NATURAL_RECTANGLE_SIZE - PIXEL_RECTANGLE_SIZE_TOLERANCE &&
  rectangle.y_max - rectangle.y_min >=
    MINIMUM_NATURAL_RECTANGLE_SIZE - PIXEL_RECTANGLE_SIZE_TOLERANCE

export const normalizedPixelMinimumSize = (
  imageSize: PixelImageDimensions
) => ({
  width:
    (MINIMUM_NATURAL_RECTANGLE_SIZE - PIXEL_RECTANGLE_SIZE_TOLERANCE) /
      imageSize.width +
    MARKUP_COORDINATE_ROUNDING_TOLERANCE,
  height:
    (MINIMUM_NATURAL_RECTANGLE_SIZE - PIXEL_RECTANGLE_SIZE_TOLERANCE) /
      imageSize.height +
    MARKUP_COORDINATE_ROUNDING_TOLERANCE,
})

export const pixelRectangleFromBounds = (
  bounds: DocumentDrawing['drawing_bounds']
): MarkupRectangle => ({
  shape: 'rectangle',
  x_min: bounds.x_start,
  y_min: bounds.y_start,
  x_max: bounds.x_end,
  y_max: bounds.y_end,
})

export const pixelImageRectangle = (
  imageSize: PixelImageDimensions
): MarkupRectangle => ({
  shape: 'rectangle',
  x_min: 0,
  y_min: 0,
  x_max: imageSize.width,
  y_max: imageSize.height,
})

export const pixelRectangleToNormalized = (
  rectangle: MarkupRectangle,
  imageSize: PixelImageDimensions
): MarkupRectangle => ({
  shape: 'rectangle',
  x_min: rectangle.x_min / imageSize.width,
  y_min: rectangle.y_min / imageSize.height,
  x_max: rectangle.x_max / imageSize.width,
  y_max: rectangle.y_max / imageSize.height,
})

export const validateMeasuredPixelRectangle = (
  rectangle: MarkupRectangle,
  imageSize: PixelImageDimensions
): PixelRectangleValidationError | undefined => {
  if (!isValidRectangle(rectangle)) return 'invalid-rectangle'
  if (
    !Number.isFinite(imageSize.width) ||
    !Number.isFinite(imageSize.height) ||
    imageSize.width <= 0 ||
    imageSize.height <= 0
  )
    return 'invalid-image-size'

  if (!pixelRectangleMeetsMinimumSize(rectangle)) return 'minimum-size'

  if (!containsRectangle(pixelImageRectangle(imageSize), rectangle))
    return 'outside-image'
}
